import React, { useState } from "react";
import { ExternalLinkIcon } from "lucide-react";
import { toast } from "sonner";
import {
  useApproveTaxExemptionMutation,
  useGetTaxExemptionsQuery,
  useRejectTaxExemptionMutation,
} from "@/store/admin_api";
import { TaxExemption, TaxExemptionStatus } from "@/model/interface/Organisation";
import { apiErrorMessage } from "@/util/apiError";
import { formatInvoiceDate } from "@/util/invoice";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const STATUS_TABS = [
  { value: "PENDING_REVIEW", label: "Pending review" },
  { value: "APPROVED", label: "Approved" },
  { value: "REJECTED", label: "Rejected" },
  { value: "ALL", label: "All" },
];

const STATUS_LABELS: Record<TaxExemptionStatus, string> = {
  PENDING_REVIEW: "Pending review",
  APPROVED: "Approved",
  REJECTED: "Rejected",
  REPLACED: "Replaced",
  WITHDRAWN: "Withdrawn",
  EXPIRED: "Expired",
};

const STATUS_PILLS: Record<TaxExemptionStatus, string> = {
  PENDING_REVIEW: "status-pill-warning",
  APPROVED: "status-pill-success",
  REJECTED: "status-pill-destructive",
  REPLACED: "status-pill-neutral",
  WITHDRAWN: "status-pill-neutral",
  EXPIRED: "status-pill-neutral",
};

function DetailField({ label, value }: { label: string; value?: string | null }) {
  if (!value) {
    return null;
  }
  return (
    <div>
      <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="text-sm">{value}</p>
    </div>
  );
}

export default function TaxExemptionsQueue() {
  const [statusTab, setStatusTab] = useState("PENDING_REVIEW");
  const { data: exemptions, isLoading } = useGetTaxExemptionsQuery(statusTab === "ALL" ? undefined : statusTab);
  const [approveExemption, { isLoading: isApproving }] = useApproveTaxExemptionMutation();
  const [rejectExemption, { isLoading: isRejecting }] = useRejectTaxExemptionMutation();
  const [reviewTarget, setReviewTarget] = useState<TaxExemption | null>(null);
  const [expiresOn, setExpiresOn] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");

  const closeReview = () => {
    setReviewTarget(null);
    setExpiresOn("");
    setRejectionReason("");
  };

  const handleApprove = async () => {
    if (!reviewTarget?.id) {
      return;
    }
    try {
      await approveExemption({ exemptionId: reviewTarget.id, expiresOn: expiresOn || null }).unwrap();
      toast.success("Exemption approved. Owners and admins were notified.");
      closeReview();
    } catch (error) {
      toast.error(apiErrorMessage(error, "Could not approve the exemption"));
    }
  };

  const handleReject = async () => {
    if (!reviewTarget?.id) {
      return;
    }
    if (!rejectionReason.trim()) {
      toast.error("Write the reason owners and admins will see");
      return;
    }
    try {
      await rejectExemption({ exemptionId: reviewTarget.id, reason: rejectionReason.trim() }).unwrap();
      toast.success("Exemption rejected. Owners and admins were notified.");
      closeReview();
    } catch (error) {
      toast.error(apiErrorMessage(error, "Could not reject the exemption"));
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4 text-foreground">
      <div>
        <h2 className="text-2xl font-bold">Tax Exemptions</h2>
        <p className="text-sm text-muted-foreground">
          Check each certificate against the billing details it was filed with. Invoices keep charging sales tax until a
          certificate is approved.
        </p>
      </div>

      <Tabs value={statusTab} onValueChange={setStatusTab}>
        <TabsList>
          {STATUS_TABS.map((tab) => (
            <TabsTrigger key={tab.value} value={tab.value}>
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="overflow-x-auto rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Organisation</TableHead>
              <TableHead>State</TableHead>
              <TableHead>Uploaded</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Expires</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!exemptions?.length && (
              <TableRow>
                <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                  No certificates
                </TableCell>
              </TableRow>
            )}
            {exemptions?.map((exemption) => (
              <TableRow key={exemption.id}>
                <TableCell className="font-medium">
                  {exemption.organisation?.name || exemption.billingName || "-"}
                </TableCell>
                <TableCell>{exemption.state || "-"}</TableCell>
                <TableCell className="text-muted-foreground">{formatInvoiceDate(exemption.createdAt)}</TableCell>
                <TableCell>
                  {exemption.status && (
                    <span className={STATUS_PILLS[exemption.status]}>{STATUS_LABELS[exemption.status]}</span>
                  )}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {exemption.expiresOn ? formatInvoiceDate(exemption.expiresOn) : "-"}
                </TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="outline" onClick={() => setReviewTarget(exemption)}>
                    {exemption.status === "PENDING_REVIEW" ? "Review" : "View"}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={reviewTarget !== null} onOpenChange={(open) => !open && closeReview()}>
        <DialogContent className="max-h-[85vh] overflow-y-auto text-foreground">
          <DialogHeader>
            <DialogTitle>{reviewTarget?.organisation?.name || reviewTarget?.billingName || "Exemption certificate"}</DialogTitle>
            <DialogDescription>
              {reviewTarget?.state ? `Exemption certificate for ${reviewTarget.state}` : "Exemption certificate"}
            </DialogDescription>
          </DialogHeader>
          {reviewTarget && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <DetailField label="Billing name" value={reviewTarget.billingName} />
                <DetailField label="EIN" value={reviewTarget.billingEin} />
                <DetailField label="Billing address" value={reviewTarget.billingAddress} />
                <DetailField label="Uploaded" value={formatInvoiceDate(reviewTarget.createdAt)} />
                <DetailField label="Reviewed" value={formatInvoiceDate(reviewTarget.reviewedAt)} />
                <DetailField label="Rejection reason" value={reviewTarget.rejectionReason} />
              </div>
              {reviewTarget.certificate?.url && (
                <Button asChild variant="outline" size="sm" className="gap-2">
                  <a href={reviewTarget.certificate.url} target="_blank" rel="noreferrer">
                    <ExternalLinkIcon className="h-4 w-4" />
                    Open {reviewTarget.certificate.name ?? "certificate"}
                  </a>
                </Button>
              )}
              {reviewTarget.status === "PENDING_REVIEW" && (
                <div className="space-y-4 border-t pt-4">
                  <div className="space-y-2">
                    <Label htmlFor="exemption-expires-on">Expiry date (optional)</Label>
                    <Input
                      id="exemption-expires-on"
                      type="date"
                      value={expiresOn}
                      onChange={(event) => setExpiresOn(event.target.value)}
                      className="w-44"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="exemption-rejection-reason">Reason if you reject it (sent to owners and admins)</Label>
                    <Textarea
                      id="exemption-rejection-reason"
                      rows={3}
                      value={rejectionReason}
                      onChange={(event) => setRejectionReason(event.target.value)}
                      placeholder="The certificate is for a different state than the billing address."
                    />
                  </div>
                </div>
              )}
            </div>
          )}
          {reviewTarget?.status === "PENDING_REVIEW" && (
            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" disabled={isRejecting} onClick={handleReject}>
                Reject
              </Button>
              <Button disabled={isApproving} onClick={handleApprove}>
                Approve
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
